import {
  ActionIcon,
  Box,
  Stack,
  Text,
  useComputedColorScheme,
} from "@mantine/core";

import MeqLogoDark from "~/assets/images/meq-logo-dark.png";
import MeqLogoLight from "~/assets/images/meq-logo-light.png";
import SvgIconRaw from "~/components/SvgIconRaw";

import classes from "./TermsOfServiceContent.module.css";

const TermsOfServiceContent = () => {
  const isDark = useComputedColorScheme("light") === "dark";

  return (
    <Box pos="relative">
      {/* Floating Download Button */}
      <ActionIcon
        component="a"
        href={`${window.location.origin}/files/Terms_of_Service_070824.pdf`}
        target="_blank"
        rel="noopener noreferrer"
        variant="default"
        size="lg"
        radius="xl"
        pos="absolute"
        top={0}
        right={0}
        style={{ zIndex: 1 }}
        title="Download PDF"
      >
        <SvgIconRaw name="Download" size={20} />
      </ActionIcon>

      <Stack gap={24}>
        <Box ta="center">
          <img
            src={isDark ? MeqLogoDark : MeqLogoLight}
            alt="MEQ"
            width={80}
            style={{ marginBottom: "8px" }}
          />
          <Text fz="1.25rem" fw={700} mb={8}>
            TERMS OF SERVICE
          </Text>
        </Box>

        <Text size="sm" mb={16}>
          <strong>MEQ PROBE PTY LTD</strong> ACN 614 314 920 (
          <strong>we, us, our</strong>) makes available a solution that
          consolidates all carcass information, data, outputs, datasets and
          images, and derives valuable insights for your management, review and
          analysis (<strong>Services</strong>) through the website located at{" "}
          <a
            href="https://meqinsights.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#1976d2" }}
          >
            https://meqinsights.com/
          </a>{" "}
          and any other technological means from time to time (
          <strong>Platform</strong>).
        </Text>

        <Text size="sm" mb={16}>
          These terms of service (<strong>Terms</strong>) constitute a legally
          binding agreement between you and us upon which we offer you access to
          the Services.
        </Text>

        <Text size="sm" fw={700} mb={8}>
          IMPORTANT:
        </Text>

        <Text size="sm" mb={16}>
          By browsing or accessing the Platform and/or consenting (upon signing
          up for an Account), you acknowledge and agree that:
        </Text>

        <Box component="ul" className={classes.list}>
          <Text component="li" size="sm">
            you have read, understood and agree to be bound by these Terms and
            our Privacy Policy available at www.meqinsights.com/privacy-policy (
            <strong>Privacy Policy</strong>);
          </Text>
          <Text component="li" size="sm">
            you consent to our collection, use, storage and disclosure of your
            Personal Information and Sensitive Information (as defined in the{" "}
            <em>Privacy Act 1988</em> (Cth)) that you provide to us, in
            accordance with our Privacy Policy;
          </Text>
          <Text component="li" size="sm">
            if you do not agree to these Terms, we will not licence the Platform
            to you and you must cease using the Platform immediately;
          </Text>
          <Text component="li" size="sm">
            users of the Platform must be 18 years of age or older. If you are
            under the age of 18, then you are not permitted to use the Platform
            and you must cease using the Platform immediately; and
          </Text>
          <Text component="li" size="sm">
            we only provide you with (through the Platform) access to the
            Services, and we make no warranties as to the accuracy or
            reliability of the information contained on the Platform, and we
            disclaim all associated liability.
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={16}>
          1. GENERAL ACKNOWLEDGMENTS AND WARRANTIES
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            We may change these Terms at any time for any reason. In the event
            of such change, we will notify you the details of the change through
            your nominated email or when you next access the Platform (where the
            terms may be displayed on-screen and you may be required to read and
            accept them to continue your use of the Services and the Platform).
          </Text>
          <Text component="li" size="sm">
            You warrant that you are, or have obtained permission from, the
            owner of the device(s) that the Platform has been accessed from (
            <strong>Devices</strong>) to be used in connection with the
            Platform. The owner of the Device may be charged by their service
            provider for internet access on the Devices. You accept
            responsibility in accordance with these Terms for the use of the
            Platform and any Services on or in relation to any Device, whether
            or not owned by you.
          </Text>
          <Text component="li" size="sm">
            The Platform may make use of location data sent from the Devices.
            You can turn off this functionality at any time by turning off the
            location services settings for the Platform on the Device. If you
            turn off location services, some functions of the Platform may not
            function. If you turn on location services, you consent to our (and
            our affiliates' and licensees') transmission, collection,
            maintenance, processing and use of your location data, in accordance
            with our Privacy Policy, to provide and improve the Services. You
            may withdraw this consent at any time by turning off the location
            services settings on the Device.
          </Text>
          <Text component="li" size="sm">
            By using this Platform, you acknowledge and agree that internet
            transmissions are never completely private or secure. You understand
            that any message or information you send using the Platform may be
            read or intercepted by others, even if there is a special notice
            that a particular transmission is encrypted.
          </Text>
          <Text component="li" size="sm">
            The Platform may contain links to other independent third-party
            websites (<strong>Third-party Sites</strong>). You acknowledge and
            agree that the Third-party Sites are not under our control, and we
            are not responsible for and do not endorse their content or their
            privacy policies (if any). You will need to make your own
            independent judgement regarding your interaction with any
            Third-party Sites, including the purchase and use of any products or
            services accessible through them.
          </Text>
          <Text component="li" size="sm">
            By using the Platform, you consent to us:
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                collecting and using technical information about the Devices,
                for the purposes of improving our Services, and providing any
                services to you; and
              </Text>
              <Text component="li" size="sm">
                monitoring your conduct while using the Platform and collecting
                information associated with your conduct to be used by us in
                relation to the Platform.
              </Text>
            </Box>
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={16}>
          2. USER ACCOUNT
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            To use the Services and Platform you may be required to set-up an
            account on the Platform (<strong>Account</strong>).
          </Text>
          <Text component="li" size="sm">
            By creating an Account, you agree:
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                you may only register for one Account;
              </Text>
              <Text component="li" size="sm">
                to submit your own valid information, which may include your
                full name, email address, mobile phone number and postal
                address;
              </Text>
              <Text component="li" size="sm">
                that all information which you submit to us will be true and
                accurate, and not misleading, deceiving or fraudulent;
              </Text>
              <Text component="li" size="sm">
                to keep confidential and secure, all aspects and information of
                your Account including your password;
              </Text>
              <Text component="li" size="sm">
                that you are responsible for any and all uses of your Account,
                whether or not you have authorised such use;
              </Text>
              <Text component="li" size="sm">
                to keep confidential and secure all aspects of other users'
                Account information that you may have access to;
              </Text>
              <Text component="li" size="sm">
                that you will not use the Services for any unlawful or otherwise
                prohibited activity;
              </Text>
              <Text component="li" size="sm">
                to maintain and promptly update your Account information to keep
                it accurate, current and complete at all times or as soon as
                reasonably practicable following any changes;
              </Text>
              <Text component="li" size="sm">
                to notify us if you discover or suspect that your Account has
                been subject to illegal, hacking or unauthorised use; and
              </Text>
              <Text component="li" size="sm">
                that you are solely responsible for maintaining the
                confidentiality of your Account and associated password(s), and
                for restricting access to your Devices, and you accept
                responsibility for all activities that occur under your Account.
              </Text>
            </Box>
          </Text>
          <Text component="li" size="sm">
            We may, from time to time (despite not having an obligation to do
            so) confirm or check the accuracy of any information provided by you
            to us. Where we make a request, you must promptly provide to us any
            information we require to confirm that the information you submit to
            us is true and accurate (and not misleading, deceiving or
            fraudulent).
          </Text>
          <Text component="li" size="sm">
            We reserve the right to refuse your access to the Platform, Services
            or an Account, terminate your Account, remove or edit Content, or
            engage in any other act that we deem necessary, at any time, for any
            reason, at our sole discretion.
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={16}>
          3. INTELLECTUAL PROPERTY
        </Text>

        <Text fz="1.25rem" fw={700} mt={8}>
          3.1. Ownership
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            You acknowledge and agree that:
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                all rights, title and interest in the Intellectual Property,
                anywhere in the world, belong to us or our licensors;
              </Text>
              <Text component="li" size="sm">
                all rights in relation to the Intellectual Property are licensed
                (not sold) to you;
              </Text>
              <Text component="li" size="sm">
                you hold no proprietary rights, title or interest in the
                Intellectual Property, other than the right to use such property
                in accordance with these Terms; and
              </Text>
              <Text component="li" size="sm">
                nothing contained in these Terms shall be construed as an
                assignment or transfer of the Intellectual Property to you.
              </Text>
            </Box>
          </Text>
          <Text component="li" size="sm">
            For the purposes of these Terms:
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                <strong>Feedback</strong> means any notes, messages, ideas,
                comments, concepts, feedback, suggestions, recommendations, or
                any other like content (in any form whatsoever) which you
                provide to us through our Platform and Services, in email or
                chat correspondence, or through any other means whatsoever,
                which relate to the functionality, operations or features of the
                Platform or Services, and any intellectual property rights
                associated with, or subsisting within, the same;
              </Text>
              <Text component="li" size="sm">
                <strong>Images</strong> means the images and all associated
                data, outputs and information that is uploaded to the Platform
                by you or us from time to time;
              </Text>
              <Text component="li" size="sm">
                <strong>Intellectual Property</strong> means all present and
                future industrial and intellectual property rights relating to
                the Platform, Images, Services, Materials, Feedback, and/or us,
                including, without limitation:
                <Box mt={8}>
                  <Text size="sm" mb={8}>
                    (A) inventions, patents, copyright, trade business, company
                    or domain names, rights in relation to registered designs,
                    registered and unregistered trade marks, know how, trade
                    secrets and the right to have confidential information kept
                    confidential, and any and all other rights to intellectual
                    property which may subsist anywhere in the world; and
                  </Text>
                  <Text size="sm">
                    (B) any application for or right to apply for registration
                    of any of those rights;
                  </Text>
                </Box>
              </Text>
              <Text component="li" size="sm">
                <strong>Materials</strong> means all of the materials displayed
                on or via the Platform, including (without limitation) all
                information, text, graphics, images, names, logos, trade marks,
                design, software and advertisements.
              </Text>
            </Box>
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={8}>
          3.2. Licences
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            We grant you a limited, royalty-free, non-exclusive,
            non-transferrable, non-sublicensable, revocable, Australia-wide
            licence to use the:
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                Intellectual Property to access the Platform and the Services;
                and
              </Text>
              <Text component="li" size="sm">
                Platform on the Devices,
              </Text>
            </Box>
            for internal purposes in connection with the necessary operation of
            your business only (and specifically excluding any commercial
            exploitation in competition with us or our Platform or Services),
            for the duration of these Terms, subject to these Terms and the
            Privacy Policy (<strong>Use Licence</strong>).
          </Text>
          <Text component="li" size="sm">
            You acknowledge and understand that you are solely responsible for
            your use of the Intellectual Property, Platform and the Services
            (including any other party's use of your Account).
          </Text>
          <Text component="li" size="sm">
            You grant us an irrevocable, perpetual, sub-licensable,
            transferable, non-exclusive, worldwide, royalty-free licence to use,
            reproduce, modify, alter, improve, distribute, publish, make
            derivative works from, and otherwise exploit (for both commercial
            and non-commercial purposes), any content that you provide to us
            through the Platform (<strong>Content</strong>), in whole or in
            part, and in any form, media or technology, whether now known or
            hereafter devised, in connection with our (and our successors',
            licensees' or affiliates') business (
            <strong>Content Licence</strong>), which shall survive the expiry or
            termination of these Terms.
          </Text>
          <Text component="li" size="sm">
            You warrant and represent that:
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                all rights, title and interest in the Content are your sole and
                exclusive property;
              </Text>
              <Text component="li" size="sm">
                the Content (and its commercial or non-commercial use) will not
                in any way infringe upon or violate the rights of any other
                party (including without limitation any rights of copyright,
                patent, trade secret or other intellectual property rights);
              </Text>
              <Text component="li" size="sm">
                but for you, no other party, or employee, contractor,
                shareholder, officer, associate and/or affiliate of you has or
                will have a legally enforceable right, title or interest in the
                Content, irrespective of whether or not the Content was created
                before, on or after these Terms; and
              </Text>
              <Text component="li" size="sm">
                your submission of the Content to us does not (and will not)
                violate or infringe any third-party rights.
              </Text>
            </Box>
          </Text>
          <Text component="li" size="sm">
            You irrevocably and unconditionally consent to (and warrant that
            your employees, agents, and contractors irrevocably and
            unconditionally consent to) all uses, acts or omissions by or on
            behalf of us, our assigns, licensees, or successors in title or any
            of its authorised persons of, or in relation to, the Feedback and
            Content, including any uses, acts or omissions which would otherwise
            breach any of your (and/or your employees', agents', or
            contractors') moral rights (as defined in Part IX of the
            <em>Copyright Act 1968</em> (Cth)).
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={8}>
          3.3. Use Licence Restrictions
        </Text>

        <Text size="sm" mb={16}>
          Except as expressly set out in these Terms or as permitted by any
          local law, you undertake and agree that you will not (and will not
          allow any person using your Account to), without our prior written
          consent:
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            rent, lease, sublicence, loan, sell or use for commercial purposes,
            the Intellectual Property, Platform and/or Services;
          </Text>
          <Text component="li" size="sm">
            modify, alter, merge, adapt, translate, reverse-engineer, decompile,
            disassemble, or make derivative uses of, the whole or any part of
            the Intellectual Property, Platform and/or Services;
          </Text>
          <Text component="li" size="sm">
            copy, imitate, mirror, reproduce, distribute, disseminate, publish,
            download, display, perform, post or transmit any Intellectual
            Property in any form or by any means;
          </Text>
          <Text component="li" size="sm">
            permit the Platform or any part of it to be combined with, or become
            incorporated in, any other programs;
          </Text>
          <Text component="li" size="sm">
            copy the Platform except where such copying is incidental to normal
            use of the Platform, or where it is necessary for the purpose of
            back-up or operational security;
          </Text>
          <Text component="li" size="sm">
            use the Intellectual Property, Platform and/or Services in a way
            that may (or does) damage, disable, overburden, interfere with, or
            adversely affect the functionality of the Intellectual Property,
            and/or Services;
          </Text>
          <Text component="li" size="sm">
            use any data mining bots, spiders, automated tools or similar data
            gathering and extraction methods, directly or indirectly, in
            connection with the Platform or Services, or collect any information
            from the Platform or Services;
          </Text>
          <Text component="li" size="sm">
            use the Intellectual Property, Platform and/or Services in a way
            that may (or does) bring our business, the Platform or Services into
            disrepute;
          </Text>
          <Text component="li" size="sm">
            use the Intellectual Property unlawfully, in an inappropriate way or
            in a manner inconsistent with these Terms;
          </Text>
          <Text component="li" size="sm">
            infringe our rights (or the rights of any third parties) in relation
            to the Intellectual Property;
          </Text>
          <Text component="li" size="sm">
            bypass or avoid our security features or measures which have been
            implemented on the Platform or in connection with the Services (or
            attempt to do the same);
          </Text>
          <Text component="li" size="sm">
            apply to register, or challenge the validity of, the Intellectual
            Property;
          </Text>
          <Text component="li" size="sm">
            provide or otherwise make available the Platform in whole or in part
            (including object and source code), in any form to any third party
            without prior written consent from us;
          </Text>
          <Text component="li" size="sm">
            disclose, share or otherwise disseminate to any third parties any
            data, results, Images, measurements, information or materials from
            the Platform; and
          </Text>
          <Text component="li" size="sm">
            fail to comply with all technology control or export laws and
            regulations that apply to the technology used or supported by the
            Platform or any Service.
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={16}>
          4. PLATFORM USE
        </Text>

        <Text fz="1.25rem" fw={700} mt={8}>
          4.1. Acceptable Use Terms
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            You must not (and will not allow any person using your Account to):
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                use the Platform or any Service in any unlawful manner, for any
                unlawful purpose, or in any manner inconsistent with these
                Terms, or act fraudulently or maliciously, for example, by
                hacking into or inserting malicious code, including viruses, or
                harmful data, into the Platform or any operating system;
              </Text>
              <Text component="li" size="sm">
                infringe our Intellectual Property or the intellectual property
                of any third party in relation to your use of the Platform or
                any Service;
              </Text>
              <Text component="li" size="sm">
                upload any Content which would be, or act in any manner that is
                (or may be seen to be) unlawful, not for a proper purpose, in
                contravention of third party rights, misleading or deceptive (or
                likely to mislead or deceive), fraudulent, or that does (or may)
                result us being vulnerable to any legal proceedings, liabilities
                or other sanctions;
              </Text>
              <Text component="li" size="sm">
                transmit any material or Content that is defamatory, offensive
                or otherwise objectionable in relation to your use of the
                Platform or Services (as determined at our sole and exclusive
                discretion);
              </Text>
              <Text component="li" size="sm">
                allow any other party to use your Account and list any Content
                on your Account and you shall be responsible for all Content on
                your Account;
              </Text>
              <Text component="li" size="sm">
                use the Platform or any Service in a way that could damage,
                disable, overburden, impair or compromise our systems or
                security or interfere with other users; and
              </Text>
              <Text component="li" size="sm">
                collect or harvest any information or data from the Platform,
                any Service or our systems or attempt to decipher any
                transmissions to or from the servers running any Service.
              </Text>
            </Box>
          </Text>
          <Text component="li" size="sm">
            You will only:
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                upload Content to the Platform yourself (or cause or permit
                Content to be uploaded to the Platform by persons who have
                agreed to comply with these Terms, who you will ensure will
                comply with these Terms);
              </Text>
              <Text component="li" size="sm">
                upload Content to the Platform that is correct and error-free,
                and you are responsible for all such Content; and
              </Text>
              <Text component="li" size="sm">
                disclose Personal Information to us regarding yourself or others
                who have consented to that disclosure and subsequent use by us
                in accordance with our Privacy Policy.
              </Text>
            </Box>
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={8}>
          4.2. General Use Acknowledgements
        </Text>

        <Text size="sm" mb={16}>
          You acknowledge and agree that:
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            we may remove, alter or amend any Content that you upload to the
            Platform at any time, for any reason, at our sole and exclusive
            discretion; and
          </Text>
          <Text component="li" size="sm">
            where you have provided contact details (through any method) of a
            person (<strong>Contact Person</strong>) we may contact that Contact
            Person in relation to the Platform to improve the Services, and you
            authorise us to contact that Contact Person via email, text, phone
            or any other method, unless you request that we provide otherwise.
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={16}>
          5. INFORMATION AND DATA
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            Our use of your Personal Information is governed by our Privacy
            Policy.
          </Text>
          <Text component="li" size="sm">
            You acknowledge and agree that:
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                we may collaborate with third-party meat processors from time to
                time to deliver carcass information, data, outputs, datasets and
                images to you through the Service and Platform (
                <strong>End Data</strong>); and
              </Text>
              <Text component="li" size="sm">
                your End Data will be accessible to you (as the authorised
                holder of your Account), and any other parties that may require
                access to such End Data in order to provide you with the Service
                and access to the Platform, including, but not limited to, any
                of your or our officers, directors, employees, agents,
                subcontractors, licensors, and suppliers or other third parties
                (and their officers, directors, employees, agents,
                subcontractors, licensors, and suppliers).
              </Text>
            </Box>
          </Text>
          <Text component="li" size="sm">
            In addition to the terms governing our use of your Personal
            Information under our Privacy Policy, you agree and consent to:
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                us sharing the End Data with the aforementioned third-party meat
                processors only for the purposes of providing you with the
                Services and access to the Platform;
              </Text>
              <Text component="li" size="sm">
                us using our authentication services in connection with your
                Personal Information and the End Data as provided from time to
                time; and
              </Text>
              <Text component="li" size="sm">
                your email address and password relating to your Account (and
                any other Personal Information) being stored through the
                'Zitadel' service by CAOS Ltd (<strong>Zitadel</strong>) and any
                other third-party providers, and you agree to abide by any terms
                and conditions prescribed by those providers from time to time
                (as applicable).
              </Text>
            </Box>
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={16}>
          6. DISCLAIMER
        </Text>

        <Text size="sm" mb={16}>
          You acknowledge and agree that:
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            the Platform has not been developed to meet your individual
            requirements;
          </Text>
          <Text component="li" size="sm">
            it is your responsibility to ensure that the processes and functions
            of the Platform meet your requirements;
          </Text>
          <Text component="li" size="sm">
            we assume no duty of care to you or any other party who relies on
            anything provided for by the Platform;
          </Text>
          <Text component="li" size="sm">
            the use of the Platform and/or Services is at your own risk, and you
            assume all risk of injury, illness, damage or loss to you or any
            other parties in connection with such use;
          </Text>
          <Text component="li" size="sm">
            the Platform and Services are provided on an "as is" and "as
            available" basis and we do not guarantee that the Platform and/or
            Services will be, available from time to time and/or suitable for
            your needs;
          </Text>
          <Text component="li" size="sm">
            we do not warrant or guarantee that the Platform will be provided in
            an uninterrupted, continuous and error-free manner;
          </Text>
          <Text component="li" size="sm">
            while we will use reasonable endeavours to ensure that the Platform
            and Services (and all information provided thereto) are up-to-date
            and current, it is not our responsibility to monitor any changes in
            generally accepted standards and we assume no liability associated
            with the same;
          </Text>
          <Text component="li" size="sm">
            we do not guarantee that any information and data made available on
            the Platform, including, but not limited to, the End Data, is
            accurate or correct, and we assume no liability associated with your
            use and reliance on that information and data;
          </Text>
          <Text component="li" size="sm">
            we rely on all warranties and undertakings provided by you in
            relation to the Services and the Platform as set out in these Terms;
          </Text>
          <Text component="li" size="sm">
            we cannot guarantee that our security procedures will be error-free,
            that transmissions of your data will always be secure or that
            unauthorised third parties will never be able to defeat our security
            measures or those of our third-party service providers;
          </Text>
          <Text component="li" size="sm">
            while we may collect Personal Information in accordance with these
            Terms, without limiting our right to make reasonable enquiries to
            ascertain the accuracy of Personal Information, at no stage are we
            responsible for (nor do we make any representations or warranties
            with respect to) the Personal Information and/or the Content, nor
            are we obligated to confirm or check the accuracy of the Personal
            Information and/or the Content; and
          </Text>
          <Text component="li" size="sm">
            we disclaim any and all warranties and representations (express or
            implied, oral or written) with respect to the Platform and/or
            Services, including any:
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                implied warranties of merchantability;
              </Text>
              <Text component="li" size="sm">
                implied warranties of fitness or suitability for any purpose;
                and
              </Text>
              <Text component="li" size="sm">
                warranties of non-infringement or condition of title.
              </Text>
            </Box>
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={16}>
          7. LIMITATION OF LIABILITY
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            To the maximum extent permitted by law, we have no liability to you
            for:
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                any loss of profit or anticipated profit, loss of data, loss of
                use, damage to goodwill, or loss due to delay, or any direct or
                indirect loss or damage (including, without limitation,
                consequential loss or damage) however caused (including, without
                limitation, due to breach of contract, negligence, statute, or
                any acts or omissions by third parties, including but not
                limited to Zitadel) which may be suffered or incurred by you or
                which may arise from or in connection with your use of the
                Platform or the Services, or your use of or reliance upon any of
                the information or the Intellectual Property provided through
                the Platform or Services, or otherwise provided by us to you;
              </Text>
              <Text component="li" size="sm">
                any losses, costs, expenses and damages (including legal costs
                and disbursements) sustained or incurred, whether directly or
                indirectly or consequentially or in any other way, arising in
                connection with:
                <Box mt={8}>
                  <Text size="sm" mb={8}>
                    (A) death, personal injury or property damage resulting
                    directly or indirectly from your use of the Platform and/or
                    Services; and/or
                  </Text>
                  <Text size="sm">
                    (B) any failure or delay due to matters beyond our
                    reasonable control.
                  </Text>
                </Box>
              </Text>
              <Text component="li" size="sm">
                any act or omission, made in error by you, in the course of
                using the Platform or the Services; and
              </Text>
              <Text component="li" size="sm">
                your Device not being a compatible with the Platform.
              </Text>
            </Box>
          </Text>
          <Text component="li" size="sm">
            Our maximum aggregate liability under or in connection with these
            Terms whether in contract, tort (including negligence) or otherwise,
            shall in all circumstances be limited to the total price paid by you
            to us in the preceding 12 months for the Services.
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={16}>
          8. EVENTS OUTSIDE OUR CONTROL
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            We will not be liable or responsible for any failure to perform, or
            delay in the performance of, any of our obligations under these
            Terms that are caused by any act or event beyond our reasonable
            control, including but not limited to, acts of God, flood, fire,
            earthquake, explosion, war, terrorism, invasion, riot or other civil
            unrest, strikes, labour stoppages or slowdowns or other industrial
            disturbances, epidemics, pandemic or passage of Law or any action
            taken by a governmental or public authority, including imposing an
            embargo (<strong>Event Outside Our Control</strong>).
          </Text>
          <Text component="li" size="sm">
            If an Event Outside Our Control takes place that affects the
            performance of our obligations under these Terms:
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                our obligations under these Terms will be suspended and the time
                for performance of our obligations will be extended for the
                duration of the Event Outside Our Control; and
              </Text>
              <Text component="li" size="sm">
                we will use our reasonable endeavours to find a solution by
                which our obligations under these Terms may be performed despite
                the Event Outside Our Control.
              </Text>
            </Box>
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={16}>
          9. TERMINATION
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            Either party may, at any time, for any reason, terminate these Terms
            immediately by written notice to the other party.
          </Text>
          <Text component="li" size="sm">
            On termination for any reason:
            <Box
              component="ol"
              pl={24}
              mt={8}
              style={{ listStyleType: "lower-roman" }}
            >
              <Text component="li" size="sm">
                all rights granted to you under these Terms shall immediately
                cease;
              </Text>
              <Text component="li" size="sm">
                you will no longer be granted access to the Services or the
                Platform;
              </Text>
              <Text component="li" size="sm">
                the Use Licence will be immediately and automatically revoked;
                and
              </Text>
              <Text component="li" size="sm">
                you must immediately return to us any Intellectual Property, and
                any of our confidential information or other property that is in
                your possession at that time.
              </Text>
            </Box>
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={16}>
          10. RELEASE AND INDEMNITY
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            You discharge and forever release us from any claim, loss, actions,
            proceedings, demands, costs, expenses and liability relating to your
            use of the Platform and/or the provision of the Services to you.
          </Text>
          <Text component="li" size="sm">
            You hereby defend, indemnify and hold harmless us, our officers,
            directors, employees, agents, subcontractors, licensors, and
            suppliers, from and against all claims, loss, actions, demands,
            liabilities and settlements, arising in connection with your use of
            the Platform and/or the Services, and/or your breach of these Terms.
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={16}>
          11. DISPUTES
        </Text>

        <Text size="sm" mb={16}>
          In the event of any dispute under these Terms:
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            you will notify us of the complaint using the contact details
            provided in these Terms;
          </Text>
          <Text component="li" size="sm">
            we will review your complaint and request any further information
            that we may require;
          </Text>
          <Text component="li" size="sm">
            you and we will both negotiate in good faith to resolve the dispute;
            and
          </Text>
          <Text component="li" size="sm">
            you agree to fully cooperate (in a timely manner) with all requests
            that we may make in the resolution process.
          </Text>
        </Box>

        <Text fz="1.25rem" fw={700} mt={16}>
          12. OTHER IMPORTANT TERMS
        </Text>

        <Box component="ol" className={classes.lowerAlphaList}>
          <Text component="li" size="sm">
            We control and operate the Platform and Services from Australia. We
            do not represent that the Platform and/or Services are appropriate
            or available for use cross-jurisdictionally. If you are accessing
            the Platform and Services from locations other than Australia, you
            acknowledge and understand that you are doing so at your own risk
            and on your own initiative, and you are solely responsible for
            compliance with local laws (as may be applicable).
          </Text>
          <Text component="li" size="sm">
            We may assign these Terms and our rights or delegate our obligations
            without your consent. All provisions contained in these Terms shall
            extend to and be binding upon you and our successors and assignees.
            You may not assign these Terms to another person or entity without
            our prior written consent.
          </Text>
          <Text component="li" size="sm">
            Enforcement of the Terms is solely at our discretion and our failure
            to enforce a provision in some instances does not constitute a
            waiver of our right to enforce such provision in other instances.
          </Text>
          <Text component="li" size="sm">
            Each of the conditions of these Terms operates separately. If any
            court or competent authority decides that any of them are unlawful
            or unenforceable, the remaining conditions will remain in full force
            and effect.
          </Text>
          <Text component="li" size="sm">
            These Terms are governed by the laws of South Australia. You and we
            both agree that the courts of South Australia will have
            non-exclusive jurisdiction.
          </Text>
        </Box>
      </Stack>
    </Box>
  );
};

export default TermsOfServiceContent;
